import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  Divider,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Tooltip as RechartsTooltip } from 'recharts';
import { Refresh as RefreshIcon, TrendingUp, Pets, LocationOn, Schedule } from '@mui/icons-material';
import { format } from 'date-fns';
import { adminApi } from '../services/api';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';
import { PetAnalyticsData } from '../types';

interface PetAnalyticsProps {
  isVisible?: boolean;
}

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#43e97b', '#38f9d7'];

const PetAnalytics: React.FC<PetAnalyticsProps> = ({ isVisible = true }) => {
  const [data, setData] = useState<PetAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminApi.getPetAnalytics();
      setData(result);
    } catch (err: any) {
      setError('Pet analitikleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Use visibility refresh hook
  const { lastRefresh, manualRefresh } = useVisibilityRefresh(fetchData, {
    refreshInterval: 60000, // 60 seconds
    isVisible,
    enabled: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Paper elevation={0} sx={{ height: 400, borderRadius: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress size={60} thickness={4} />
        </Box>
      </Paper>
    );
  }

  if (error || !data) {
    return (
      <Paper elevation={0} sx={{ height: 400, borderRadius: 3 }}>
        <Box p={3}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error || 'Veri yüklenemedi'}
          </Alert>
        </Box>
      </Paper>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Modern Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4, 
          p: 3, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Pet Analitikleri
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Hayvan sahiplenme ve kayıp pet istatistikleri
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Box textAlign="right">
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Son güncelleme
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {format(lastRefresh, 'HH:mm:ss')}
              </Typography>
            </Box>
            <Tooltip title="Yenile">
              <IconButton 
                onClick={manualRefresh} 
                disabled={loading}
                sx={{ 
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Modern Grid Layout */}
      <Box 
        display="grid" 
        gridTemplateColumns={{ 
          xs: '1fr', 
          sm: 'repeat(2, 1fr)', 
          md: 'repeat(3, 1fr)', 
          lg: 'repeat(3, 1fr)',
          xl: 'repeat(4, 1fr)'
        }} 
        gap={3}
        sx={{ mb: 3 }}
      >
        {/* Adoption Rate - Minimal Card */}
        <Box 
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            p: 3,
            backgroundColor: 'white'
          }}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <TrendingUp sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Sahiplenme Oranı
            </Typography>
          </Box>
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 1, color: 'primary.main' }}>
            %{data.adoptionRate}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.totalAdopted} hayvan sahiplendirildi
          </Typography>
        </Box>

        {/* Pet Types - Minimal Chart */}
        <Box 
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            p: 3,
            backgroundColor: 'white'
          }}
        >
          <Box display="flex" alignItems="center" mb={3}>
            <Pets sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Hayvan Türleri
            </Typography>
          </Box>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={data.petTypes}
                cx="50%"
                cy="50%"
                outerRadius={60}
                innerRadius={20}
                fill="#8884d8"
                dataKey="count"
                label={(entry: any) => `${entry._id}`}
              >
                {data.petTypes.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip 
                formatter={(value: any) => [`${value} adet`, 'Sayı']}
                labelStyle={{ color: '#333' }}
                contentStyle={{
                  borderRadius: 8,
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {/* Popular Breeds - Minimal Bar Chart */}
        <Box 
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            p: 3,
            backgroundColor: 'white'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            En Popüler Irklar
          </Typography>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.popularBreeds.slice(0, 6)}>
              <CartesianGrid strokeDasharray="1 1" stroke="#f0f0f0" />
              <XAxis 
                dataKey="_id" 
                angle={-45}
                textAnchor="end"
                height={60}
                fontSize={10}
                stroke="#666"
              />
              <YAxis fontSize={10} stroke="#666" />
              <RechartsTooltip 
                formatter={(value: any) => [`${value} adet`, 'Sayı']}
                labelStyle={{ color: '#333' }}
                contentStyle={{
                  borderRadius: 8,
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
              <Bar 
                dataKey="count" 
                fill="#667eea" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* City Distribution - Minimal List */}
        <Box 
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            p: 3,
            backgroundColor: 'white'
          }}
        >
          <Box display="flex" alignItems="center" mb={3}>
            <LocationOn sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Şehir Dağılımı
            </Typography>
          </Box>
          <Stack spacing={1}>
            {data.cityDistribution.slice(0, 6).map((city, index) => (
              <Box 
                key={city._id} 
                display="flex" 
                justifyContent="space-between" 
                alignItems="center"
                sx={{ 
                  p: 1.5, 
                  borderRadius: 1, 
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  minHeight: 36
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500,
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mr: 1
                  }}
                >
                  {city._id || 'Belirtilmemiş'}
                </Typography>
                <Box sx={{ flexShrink: 0 }}>
                  <Chip 
                    label={city.count}
                    size="small"
                    sx={{ 
                      backgroundColor: COLORS[index % COLORS.length],
                      color: 'white',
                      fontWeight: 600,
                      height: 24,
                      fontSize: '0.75rem',
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Adoption Breakdown - Minimal Stats */}
        <Box 
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            p: 3,
            backgroundColor: 'white'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Sahiplenme Detayları
          </Typography>
          <Stack spacing={2}>
            <Box 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: 'primary.50',
                border: '1px solid',
                borderColor: 'primary.200'
              }}
            >
              <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 600, mb: 1 }}>
                Genel İstatistikler
              </Typography>
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {data.adoptionBreakdown.totalPets}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Toplam Hayvan
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                    {data.adoptionBreakdown.waitingForApproval}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Onay Bekleyen
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            <Divider />
            
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Onay Durumu
              </Typography>
              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Onaylanan</Typography>
                  <Chip 
                    label={data.adoptionBreakdown.approved} 
                    size="small" 
                    sx={{ backgroundColor: 'success.main', color: 'white', height: 24 }}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Reddedilen</Typography>
                  <Chip 
                    label={data.adoptionBreakdown.rejected} 
                    size="small" 
                    sx={{ backgroundColor: 'error.main', color: 'white', height: 24 }}
                  />
                </Box>
              </Stack>
            </Box>
            
            <Divider />
            
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Sahiplenme Türleri
              </Typography>
              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Patigo Üzerinden</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {data.adoptionBreakdown.adoptedThroughPatigo}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Dış Platform</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {data.adoptionBreakdown.adoptedOutsidePatigo}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* Lost Pet Statistics - Minimal Stats */}
        <Box 
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            p: 3,
            backgroundColor: 'white'
          }}
        >
          <Box display="flex" alignItems="center" mb={3}>
            <Schedule sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Kayıp Hayvan İstatistikleri
            </Typography>
          </Box>
          <Stack spacing={2}>
            <Box 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                backgroundColor: 'info.50',
                border: '1px solid',
                borderColor: 'info.200'
              }}
            >
              <Typography variant="subtitle2" color="info.main" sx={{ fontWeight: 600, mb: 1 }}>
                Genel Durum
              </Typography>
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                    {data.lostPetStats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Toplam Rapor
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {data.lostPetStats.found}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bulunan
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            <Divider />
            
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Durum Dağılımı
              </Typography>
              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Aktif</Typography>
                  <Chip 
                    label={data.lostPetStats.active} 
                    size="small" 
                    sx={{ backgroundColor: 'warning.main', color: 'white', height: 24 }}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">İptal Edilen</Typography>
                  <Chip 
                    label={data.lostPetStats.cancelled} 
                    size="small" 
                    sx={{ backgroundColor: 'error.main', color: 'white', height: 24 }}
                  />
                </Box>
              </Stack>
            </Box>
            
            <Divider />
            
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Onay Durumu
              </Typography>
              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Onay Bekleyen</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {data.lostPetStats.waitingApproval}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Onaylanan</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {data.lostPetStats.approved}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Reddedilen</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {data.lostPetStats.rejected}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* Age Distribution - Minimal Chart */}
        <Box 
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            p: 3,
            backgroundColor: 'white'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Yaş Dağılımı
          </Typography>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data.ageDistribution}>
              <CartesianGrid strokeDasharray="1 1" stroke="#f0f0f0" />
              <XAxis 
                dataKey="_id" 
                angle={-45}
                textAnchor="end"
                height={50}
                fontSize={10}
                stroke="#666"
              />
              <YAxis fontSize={10} stroke="#666" />
              <RechartsTooltip 
                formatter={(value: any) => [`${value} adet`, 'Sayı']}
                labelStyle={{ color: '#333' }}
                contentStyle={{
                  borderRadius: 8,
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
              <Bar 
                dataKey="count" 
                fill="#43e97b" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default PetAnalytics;
